import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  adsEnabled,
  hasStoredSession,
  installGameAdsBridge,
  onAdsChange,
  setMembership,
} from './adsGate';
import { isSyncKey, snapshot, summarize } from './progressSync';

// Account state for the whole site. The Supabase client (src/account/client.js)
// is only downloaded when the visitor has a stored session, opens an /account
// page or comes back from Sign in with Apple, so signed-out visitors pay
// nothing for it.
//
// Signed-in players also get their game progress synced (progressSync.js):
// one pass at page load (games wait for it, see useGameFrame.js), then uploads
// when a game saves, when the tab is hidden and every 90 s while visible.

const AccountContext = createContext(null);

// Pages that need the account client even for visitors with no stored session.
const isAccountPath = (pathname) => /^\/(account|plus)(\/|$)/.test(pathname);
const FIRST_SYNC_TIMEOUT_MS = 4000;

// Coming back from Sign in with Apple, Supabase puts the session in the URL
// (#access_token=... with the implicit flow, ?code=... with PKCE) for
// detectSessionInUrl to consume, or reports a failure as error /
// error_description in the query or the hash.
const AUTH_ERROR_PARAMS = ['error', 'error_code', 'error_description'];
const hasAuthTokens = () => /(^#|&)access_token=/.test(window.location.hash)
  || /[?&]code=/.test(window.location.search);

function friendlyAuthError(params) {
  const text = AUTH_ERROR_PARAMS.map((k) => params.get(k) || '').join(' ').toLowerCase();
  if (text.includes('cancel')) return 'Sign-in was cancelled. You can try again, or sign in with your email.';
  if (text.includes('expired')) return 'That sign-in link has expired. Please sign in again.';
  return 'We could not sign you in. Please try again, or sign in with your email.';
}

/**
 * Reads a failed sign-in from the URL and removes it (keeping any other query,
 * such as ?return=). Returns a message to show, or ''.
 */
function takeAuthErrorFromUrl() {
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  const found = AUTH_ERROR_PARAMS.some((k) => url.searchParams.has(k) || hash.has(k));
  if (!found) return '';
  const merged = new URLSearchParams(hash);
  url.searchParams.forEach((v, k) => merged.set(k, v));
  AUTH_ERROR_PARAMS.forEach((k) => { url.searchParams.delete(k); hash.delete(k); });
  const rest = hash.toString();
  url.hash = rest ? `#${rest}` : '';
  window.history.replaceState(window.history.state, '', url.toString());
  return friendlyAuthError(merged);
}

/** After detectSessionInUrl took the tokens: drop the leftover '#' (or tokens). */
function cleanAuthTokensFromUrl() {
  const { href, hash, pathname, search } = window.location;
  if (href.endsWith('#') || /(^#|&)(access_token|refresh_token)=/.test(hash)) {
    window.history.replaceState(window.history.state, '', pathname + search);
  }
}

const CHANGE_DEBOUNCE_MS = 4000;
const PERIODIC_SYNC_MS = 90000;

export function AccountProvider({ children }) {
  const { pathname } = useLocation();
  const [state, setState] = useState({
    ready: false,   // a session check has finished
    user: null,
    member: false,
    expiresAt: null,
    checkError: false,
  });
  // A failed Sign in with Apple return, shown by SignInForm.
  const [authError, setAuthError] = useState('');
  const [ads, setAds] = useState(adsEnabled);
  const clientRef = useRef(null);
  const loadingRef = useRef(null);
  // Game progress: `ready` gates game frames until the first pass is done.
  const [progress, setProgress] = useState(() => ({
    ready: typeof window === 'undefined' || !hasStoredSession(),
    status: 'idle', // idle | syncing | error
    lastSyncedAt: null,
    summary: null,
  }));
  const userRef = useRef(null);
  const gameRef = useRef(null); // { id, name, snap } while a game frame is open
  const queueRef = useRef(Promise.resolve());
  const firstSyncRef = useRef(false);

  const runSync = useCallback(() => {
    const job = queueRef.current.then(async () => {
      const client = clientRef.current;
      if (!client || !userRef.current) return;
      setProgress((p) => ({ ...p, status: 'syncing' }));
      try {
        const g = gameRef.current;
        const res = await client.syncProgress({
          applyRemote: () => !gameRef.current,
          game: g ? { id: g.id, name: g.name } : null,
          changedSince: g ? g.snap : null,
        });
        setProgress((p) => ({
          ...p,
          status: 'idle',
          lastSyncedAt: res ? Date.now() : p.lastSyncedAt,
          summary: res ? summarize(res.rows, window.localStorage) : p.summary,
        }));
      } catch (e) {
        setProgress((p) => ({ ...p, status: 'error' }));
      }
    });
    queueRef.current = job.catch(() => {});
    return job;
  }, []);

  const markReady = useCallback(() => {
    setProgress((p) => (p.ready ? p : { ...p, ready: true }));
  }, []);

  const refresh = useCallback(async (client, session) => {
    userRef.current = session?.user || null;
    if (session && !firstSyncRef.current) {
      firstSyncRef.current = true;
      const timer = setTimeout(markReady, FIRST_SYNC_TIMEOUT_MS);
      runSync().finally(() => { clearTimeout(timer); markReady(); });
    } else if (!session) {
      firstSyncRef.current = false; // the next sign-in syncs straight away
      markReady();
    }
    if (!session) {
      setMembership({ uid: null, member: false });
      setState({ ready: true, user: null, member: false, expiresAt: null, checkError: false });
      return;
    }
    const { member, expiresAt, error } = await client.fetchPlus();
    if (error) {
      // Network or server trouble: keep whatever the cache says.
      setState((s) => ({ ...s, ready: true, user: session.user, checkError: true }));
      return;
    }
    setMembership({ uid: session.user.id, member, expiresAt });
    setState({ ready: true, user: session.user, member, expiresAt, checkError: false });
  }, [markReady, runSync]);

  const loadClient = useCallback(() => {
    if (clientRef.current) return Promise.resolve(clientRef.current);
    if (!loadingRef.current) {
      loadingRef.current = import('./client.js').then(async (client) => {
        clientRef.current = client;
        client.onAuthChange((session) => { refresh(client, session); });
        // getSession() waits for the client to consume any session in the URL.
        await refresh(client, await client.getSession());
        cleanAuthTokensFromUrl();
        return client;
      });
    }
    return loadingRef.current;
  }, [refresh]);

  useEffect(() => {
    installGameAdsBridge();
    return onAdsChange(setAds);
  }, []);

  // Uploads: a game in a frame saved something (the 'storage' event reaches
  // this page because the frame is a different document on the same origin),
  // the tab is being hidden or closed, or periodically while visible.
  useEffect(() => {
    let timer = null;
    const signedIn = () => clientRef.current && userRef.current;
    const onStorage = (e) => {
      if (!signedIn() || (e.key && !isSyncKey(e.key))) return;
      clearTimeout(timer);
      timer = setTimeout(runSync, CHANGE_DEBOUNCE_MS);
    };
    const onHide = () => {
      if (signedIn() && document.visibilityState === 'hidden') runSync();
    };
    const onPageHide = () => { if (signedIn()) runSync(); };
    const interval = setInterval(() => {
      if (signedIn() && document.visibilityState === 'visible') runSync();
    }, PERIODIC_SYNC_MS);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [runSync]);

  /** A game frame finished loading: tag what it changes from now on. */
  const startGame = useCallback((id, name) => {
    gameRef.current = { id, name, snap: snapshot(window.localStorage) };
  }, []);

  /** The game frame is going away: upload what it saved, then stop tagging. */
  const stopGame = useCallback((id) => {
    if (!gameRef.current || gameRef.current.id !== id) return;
    const done = () => { if (gameRef.current?.id === id) gameRef.current = null; };
    if (clientRef.current && userRef.current) runSync().finally(done);
    else done();
  }, [runSync]);

  // Runs before the loader below. An error return is removed from the URL
  // first, so the client starts normally and keeps any existing session.
  useEffect(() => {
    const message = takeAuthErrorFromUrl();
    if (message) setAuthError(message);
  }, []);

  const clearAuthError = useCallback(() => setAuthError(''), []);

  useEffect(() => {
    if (clientRef.current || loadingRef.current) return;
    if (hasStoredSession() || isAccountPath(pathname) || hasAuthTokens()) {
      loadClient();
    } else if (!adsEnabled()) {
      // A cached "member" answer with no session left (signed out in another
      // tab, or storage cleared): back to the normal signed-out experience.
      setMembership({ uid: null, member: false });
      setState((s) => ({ ...s, ready: true }));
    }
  }, [pathname, loadClient]);

  // Re-reads membership now (e.g. after a checkout, before the webhook lands).
  const recheck = useCallback(async () => {
    const client = await loadClient();
    await refresh(client, await client.getSession());
  }, [loadClient, refresh]);

  const value = {
    ...state, adsEnabled: ads, loadClient, recheck, authError, clearAuthError,
    progress, progressReady: progress.ready, startGame, stopGame, syncProgress: runSync,
  };
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

const FALLBACK = {
  ready: false, user: null, member: false, expiresAt: null, checkError: false,
  adsEnabled: true, loadClient: () => Promise.reject(new Error('no AccountProvider')),
  recheck: () => Promise.resolve(), authError: '', clearAuthError: () => {},
  progress: { ready: true, status: 'idle', lastSyncedAt: null, summary: null },
  progressReady: true, startGame: () => {}, stopGame: () => {}, syncProgress: () => Promise.resolve(),
};

export function useAccount() {
  return useContext(AccountContext) || FALLBACK;
}

/** True after mount while a stored session is still being checked. */
export function useSessionPending() {
  const { ready } = useAccount();
  // Read storage after mount only, so the first render matches the prerendered HTML.
  const [stored, setStored] = useState(false);
  useEffect(() => { setStored(hasStoredSession()); }, []);
  return stored && !ready;
}
